import * as moment from 'moment';
import * as React from 'react';

import { DASHBOARD_DATE_FORMAT } from './Dashboard.constants';

export interface RangePreset {
  endDate: string;
  key: string;
  label: string;
  startDate: string;
}

interface DashboardRangePresetsProps {
  endDate: string;
  onSelect: (startDate: string, endDate: string) => void;
  startDate: string;
}

interface RangePresetButtonProps {
  isActive: boolean;
  onSelect: (startDate: string, endDate: string) => void;
  preset: RangePreset;
}

const LAST_SEVEN_DAYS = 7;
const LAST_THIRTY_DAYS = 30;

const asDay = (value: moment.Moment): string => value.format(DASHBOARD_DATE_FORMAT);

/**
 * Every preset is derived from `today` rather than hardcoded, so the list stays correct without a
 * page reload and can be verified against any date in a test.
 *
 * Two rules run through all of them:
 *
 * - Weeks start on **Monday** (`isoWeek`), matching getStartOfMondayInWeek in common which the
 *   weekly driver salary report already uses. moment's plain `startOf('week')` would follow the
 *   browser locale and land on Sunday for most users.
 * - Ranges covering the present are **clamped to today**. "Bulan ini" on the 9th means 1–9, not
 *   1–30: padding the rest of the month with days that cannot have data would drag every average
 *   down and make the comparison against the previous period meaningless.
 *
 * All eight stay within the 31-day cap the filter and the backend both enforce — the longest,
 * a 31-day "Bulan lalu", measures 30 by the toolbar's floor-based duration check.
 */
export const buildRangePresets = (today: moment.Moment): RangePreset[] => {
  const yesterday = today.clone().subtract(1, 'day');
  const lastWeekStart = today
    .clone()
    .subtract(1, 'week')
    .startOf('isoWeek');
  const lastMonthStart = today
    .clone()
    .subtract(1, 'month')
    .startOf('month');

  return [
    { key: 'today', label: 'Hari ini', startDate: asDay(today), endDate: asDay(today) },
    { key: 'yesterday', label: 'Kemarin', startDate: asDay(yesterday), endDate: asDay(yesterday) },
    {
      key: 'thisWeek',
      label: 'Minggu ini',
      startDate: asDay(today.clone().startOf('isoWeek')),
      endDate: asDay(today)
    },
    {
      key: 'lastWeek',
      label: 'Minggu lalu',
      startDate: asDay(lastWeekStart),
      endDate: asDay(lastWeekStart.clone().endOf('isoWeek'))
    },
    {
      key: 'thisMonth',
      label: 'Bulan ini',
      startDate: asDay(today.clone().startOf('month')),
      endDate: asDay(today)
    },
    {
      key: 'lastMonth',
      label: 'Bulan lalu',
      startDate: asDay(lastMonthStart),
      endDate: asDay(lastMonthStart.clone().endOf('month'))
    },
    {
      key: 'last7',
      label: '7 hari terakhir',
      startDate: asDay(today.clone().subtract(LAST_SEVEN_DAYS - 1, 'days')),
      endDate: asDay(today)
    },
    {
      key: 'last30',
      label: '30 hari terakhir',
      startDate: asDay(today.clone().subtract(LAST_THIRTY_DAYS - 1, 'days')),
      endDate: asDay(today)
    }
  ];
};

/**
 * A class so the click handler is bound per instance — tslint's jsx-no-lambda forbids building one
 * inline while mapping, the same reason GranularityToggle is a class.
 */
class RangePresetButton extends React.PureComponent<RangePresetButtonProps> {
  render() {
    const { isActive, preset } = this.props;
    return (
      <button
        className={`Dashboard__preset${isActive ? ' Dashboard__preset--active' : ''}`}
        onClick={this.handleClick}
        type="button"
      >
        {preset.label}
      </button>
    );
  }

  private readonly handleClick = () => this.props.onSelect(this.props.preset.startDate, this.props.preset.endDate);
}

/**
 * Shortcuts for the ranges ops asks for daily. The date pickers stay: a request like "dua minggu
 * awal September" is not a preset, because a preset is always relative to today.
 */
export const DashboardRangePresets = (props: DashboardRangePresetsProps) => {
  const { endDate, onSelect, startDate } = props;
  const presets = buildRangePresets(moment());

  return (
    <div className="Dashboard__preset-group">
      {presets.map((preset: RangePreset) => (
        <RangePresetButton
          isActive={preset.startDate === startDate && preset.endDate === endDate}
          key={preset.key}
          onSelect={onSelect}
          preset={preset}
        />
      ))}
    </div>
  );
};
